import { API_ENDPOINTS, DEFAULT_PAGINATION } from '@/config/constants';
import { StorageUtils } from '@utils/storageUtils';
import {
  ApiResponse,
  Student,
  CreateStudentInput,
  UpdateStudentInput,
  Course,
  CreateCourseInput,
  UpdateCourseInput,
  Mapping,
  CreateMappingInput,
  LoginResponse,
  PaginationParams,
  UserProfile,
} from '../types/index';
import { EnvVariables } from '@/config/env-helper';

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = EnvVariables.apiBaseUrl;
  }

  /**
   * Get headers with authorization token
   */
  private getHeaders(): HeadersInit {
    const token = StorageUtils.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Generic request handler
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: this.getHeaders(),
      });

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }


  async userLogin(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>(API_ENDPOINTS.ADMIN_LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getUserProfile(): Promise<ApiResponse<{ user: UserProfile }>> {
    return this.request<{ user: UserProfile }>(API_ENDPOINTS.ADMIN_PROFILE);
  }

  async getStudents(params?: PaginationParams): Promise<ApiResponse<{ students: Student[] }>> {
    const queryParams = new URLSearchParams({
      pageNumber: String(params?.pageNumber || DEFAULT_PAGINATION.pageNumber),
      recLimit: String(params?.recLimit || DEFAULT_PAGINATION.recLimit),
      ...(params?.search && { search: params.search }),
    });

    return this.request<{ students: Student[] }>(`${API_ENDPOINTS.STUDENTS}?${queryParams}`);
  }

  async getStudent(studentId: string): Promise<ApiResponse<Student>> {
    return this.request<Student>(API_ENDPOINTS.STUDENT_BY_ID(studentId));
  }

  async createStudent(data: CreateStudentInput): Promise<ApiResponse<Student>> {
    return this.request<Student>(API_ENDPOINTS.STUDENTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStudent(
    studentId: string,
    data: UpdateStudentInput
  ): Promise<ApiResponse<Student>> {
    return this.request<Student>(API_ENDPOINTS.STUDENT_BY_ID(studentId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteStudent(studentId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(API_ENDPOINTS.STUDENT_BY_ID(studentId), {
      method: 'DELETE',
    });
  }

  async getCourses(params?: PaginationParams): Promise<ApiResponse<{ courses: Course[] }>> {
    const queryParams = new URLSearchParams({
      pageNumber: String(params?.pageNumber || DEFAULT_PAGINATION.pageNumber),
      recLimit: String(params?.recLimit || DEFAULT_PAGINATION.recLimit),
    });

    return this.request<{ courses: Course[] }>(`${API_ENDPOINTS.COURSES}?${queryParams}`);
  }

  async getCourse(courseId: string): Promise<ApiResponse<Course>> {
    return this.request<Course>(API_ENDPOINTS.COURSE_BY_ID(courseId));
  }

  async createCourse(data: CreateCourseInput): Promise<ApiResponse<Course>> {
    return this.request<Course>(API_ENDPOINTS.COURSES, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCourse(courseId: string, data: UpdateCourseInput): Promise<ApiResponse<Course>> {
    return this.request<Course>(API_ENDPOINTS.COURSE_BY_ID(courseId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCourse(courseId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(API_ENDPOINTS.COURSE_BY_ID(courseId), {
      method: 'DELETE',
    });
  }

  async getMappings(params?: PaginationParams): Promise<ApiResponse<{ mappings: Mapping[] }>> {
    const queryParams = new URLSearchParams({
      pageNumber: String(params?.pageNumber || DEFAULT_PAGINATION.pageNumber),
      recLimit: String(params?.recLimit || DEFAULT_PAGINATION.recLimit),
    });

    return this.request<{ mappings: Mapping[] }>(`${API_ENDPOINTS.MAPPINGS}?${queryParams}`);
  }

  async getMapping(mappingId: string): Promise<ApiResponse<Mapping>> {
    return this.request<Mapping>(API_ENDPOINTS.MAPPING_BY_ID(mappingId));
  }

  async createMapping(data: CreateMappingInput): Promise<ApiResponse<Mapping>> {
    return this.request<Mapping>(API_ENDPOINTS.MAPPINGS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteMapping(mappingId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(API_ENDPOINTS.MAPPING_BY_ID(mappingId), {
      method: 'DELETE',
    });
  }

  async previewCertificate(mappingId: string): Promise<string> {
    const token = StorageUtils.getAuthToken();
    const response = await fetch(`${this.baseURL}${API_ENDPOINTS.CERTIFICATE_PREVIEW(mappingId)}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to preview certificate');
    }

    return response.text();
  }

  async downloadCertificate(mappingId: string): Promise<Blob> {
    const token = StorageUtils.getAuthToken();
    const response = await fetch(
      `${this.baseURL}${API_ENDPOINTS.CERTIFICATE_DOWNLOAD(mappingId)}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download certificate');
    }

    return response.blob();
  }

  async validateCredential(credentialId: string): Promise<ApiResponse<{ certificate: Mapping }>> {
    return this.request<{ certificate: Mapping }>(API_ENDPOINTS.VALIDATE(credentialId));
  }
}

export const apiService = new ApiService();
export default apiService;